import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Space, Spin } from 'antd';
import { CameraOutlined, CloseOutlined } from '@ant-design/icons';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';

interface QuetQrCameraProps {
    /** Có đang hiển thị/mount camera không */
    visible: boolean;
    /** Callback khi quét được mã QR */
    onScanSuccess: (decodedText: string) => void;
    /** Callback khi đóng camera */
    onClose: () => void;
    /** Có đang xử lý không (tránh quét lặp) */
    processing?: boolean;
}

const SCANNER_REGION_ID = 'qr-camera-reader';
const SCAN_COOLDOWN_MS = 2000;

const QuetQrCamera: React.FC<QuetQrCameraProps> = ({ visible, onScanSuccess, onClose, processing }) => {
    const scannerRef = useRef<Html5Qrcode | null>(null);
    const [error, setError] = useState<string>('');
    const [starting, setStarting] = useState<boolean>(false);
    const lastScanTime = useRef<number>(0);
    const mountedRef = useRef<boolean>(true);

    const stopScanner = async () => {
        try {
            if (scannerRef.current) {
                const state = scannerRef.current.getState();
                if (state === Html5QrcodeScannerState.SCANNING || state === Html5QrcodeScannerState.PAUSED) {
                    await scannerRef.current.stop();
                }
                scannerRef.current.clear();
                scannerRef.current = null;
            }
        } catch (err) {
            // Bỏ qua lỗi khi stop/clear
            console.warn('Lỗi khi dừng camera:', err);
            scannerRef.current = null;
        }
    };

    const startScanner = async () => {
        setError('');
        setStarting(true);

        // Kiểm tra trình duyệt hỗ trợ camera
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            setError('Trình duyệt không hỗ trợ camera. Vui lòng dùng HTTPS hoặc dùng ô quét mã bằng máy scan.');
            setStarting(false);
            return;
        }

        try {
            // Đợi DOM render
            await new Promise(resolve => setTimeout(resolve, 300));

            if (!mountedRef.current) return;

            const element = document.getElementById(SCANNER_REGION_ID);
            if (!element) {
                setStarting(false);
                return;
            }

            const scanner = new Html5Qrcode(SCANNER_REGION_ID);
            scannerRef.current = scanner;

            await scanner.start(
                { facingMode: 'environment' },
                {
                    fps: 10,
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0,
                },
                (decodedText) => {
                    const now = Date.now();
                    if (now - lastScanTime.current < SCAN_COOLDOWN_MS) return;
                    lastScanTime.current = now;
                    onScanSuccess(decodedText);
                },
                () => {
                    // Không tìm thấy mã QR - bỏ qua
                }
            );

            if (mountedRef.current) {
                setStarting(false);
            }
        } catch (err: any) {
            if (!mountedRef.current) return;
            setStarting(false);

            if (err?.name === 'NotAllowedError' || err?.message?.includes('Permission')) {
                setError('Chưa cấp quyền truy cập camera. Vui lòng cho phép truy cập camera trong cài đặt trình duyệt, hoặc dùng ô quét mã bằng máy scan.');
            } else if (err?.name === 'NotFoundError' || err?.message?.includes('Requested device not found')) {
                setError('Không tìm thấy camera. Vui lòng kiểm tra thiết bị hoặc dùng ô quét mã bằng máy scan.');
            } else if (err?.name === 'NotReadableError' || err?.message?.includes('Could not start')) {
                setError('Camera đang được sử dụng bởi ứng dụng khác. Vui lòng đóng ứng dụng khác và thử lại.');
            } else if (err?.message?.includes('SSL') || err?.message?.includes('secure')) {
                setError('Camera yêu cầu kết nối HTTPS. Vui lòng truy cập qua HTTPS hoặc dùng ô quét mã bằng máy scan.');
            } else {
                setError(`Không thể khởi động camera: ${err?.message || 'Lỗi không xác định'}. Vui lòng dùng ô quét mã bằng máy scan.`);
            }
        }
    };

    useEffect(() => {
        mountedRef.current = true;

        if (visible) {
            startScanner();
        }

        return () => {
            mountedRef.current = false;
            stopScanner();
        };
    }, [visible]);

    if (!visible) return null;

    return (
        <div>
            {starting && (
                <div style={{ textAlign: 'center', padding: 20 }}>
                    <Spin tip="Đang khởi động camera..." />
                </div>
            )}

            {error && (
                <Alert
                    message="Lỗi camera"
                    description={error}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    action={
                        <Space direction="vertical">
                            <Button size="small" onClick={() => { stopScanner(); startScanner(); }}>
                                Thử lại
                            </Button>
                            <Button size="small" onClick={onClose}>
                                Đóng
                            </Button>
                        </Space>
                    }
                />
            )}

            <div
                id={SCANNER_REGION_ID}
                style={{
                    width: '100%',
                    maxWidth: 500,
                    margin: '0 auto',
                    display: error ? 'none' : 'block',
                }}
            />

            {processing && (
                <div style={{ textAlign: 'center', padding: 10 }}>
                    <Spin tip="Đang xử lý mã QR..." />
                </div>
            )}

            <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button icon={<CloseOutlined />} onClick={onClose}>
                    Đóng camera
                </Button>
            </div>
        </div>
    );
};

export default QuetQrCamera;
